import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltIconDuplicateOutline } from '@cae/cobalt-react/icons/outline/CobaltIconDuplicateOutline';
import { CobaltIconCheckmarkCircleSolid } from '@cae/cobalt-react/icons/solid/CobaltIconCheckmarkCircleSolid';
import * as React from 'react';

import type { CopyButtonProps } from './CopyButton.types';

import styles from './CopyButton.module.css';

const CopyButton = React.memo(
  ({ value, ariaLabel }: Readonly<CopyButtonProps>): React.ReactElement => {
    const [copied, setCopied] = React.useState<boolean>(false);

    React.useEffect((): (() => void) | void => {
      if (!copied) return;
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }, [copied]);

    const handleCopy = React.useCallback(
      async (e: React.MouseEvent): Promise<void> => {
        e.stopPropagation();
        if (
          !navigator.clipboard ||
          typeof navigator.clipboard.writeText !== 'function'
        ) {
          return;
        }
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
        } catch (err) {
          console.error('Failed to copy to clipboard', err);
        }
      },
      [value]
    );

    return (
      <span className={styles.copyButtonWrapper} data-testid="copy-button">
        <span role="status" aria-live="polite" className={styles.srOnly}>
          {copied ? 'Copied!' : ''}
        </span>
        <CobaltButton
          type="button"
          variant="ghost"
          data-copy-btn
          className={`${styles.copyButton}${copied ? ` ${styles.copyButtonHidden}` : ''}`}
          onClick={handleCopy}
          aria-label={ariaLabel}
        >
          <CobaltIconDuplicateOutline
            aria-hidden="true"
            className={`${styles.copyIcon}${copied ? ` ${styles.copyIconHidden}` : ''}`}
          />
        </CobaltButton>
        {copied && (
          <span className={styles.copiedInline} aria-hidden="true">
            <CobaltIconCheckmarkCircleSolid className={styles.iconCopied} />
            Copied!
          </span>
        )}
      </span>
    );
  }
);

CopyButton.displayName = 'CopyButton';

export { CopyButton };
